/**
 * CEP lookup via BFF (ezy-bff GET /cep/:code). BFF proxies to ViaCEP.
 * No auth required. Uses same PRD_BFF_URL as auth.
 */

import { getAuthConfig } from "@/config/auth";

/** ViaCEP/BFF response when CEP is found. */
export interface CepResponse {
  cep?: string;
  logradouro: string;
  complemento?: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge?: string;
  gia?: string;
  ddd?: string;
  siafi?: string;
}

/** Response when CEP is not found (BFF still returns 200). */
export interface CepNotFoundResponse {
  erro: true;
}

/**
 * Fetches address data for a Brazilian CEP from the BFF (which proxies to ViaCEP).
 * @param cep - 8 digits, with or without hyphen (e.g. "01310100" or "01310-100")
 * @returns Address fields (street, neighborhood, city, state) or throws
 */
export async function fetchCep(cep: string): Promise<{
  street: string;
  neighborhood: string;
  city: string;
  state: string;
}> {
  const digits = cep.replace(/\D/g, "");
  if (digits.length !== 8) {
    throw new Error("CEP deve ter 8 dígitos.");
  }
  const { baseUrl } = getAuthConfig();
  if (!baseUrl) {
    throw new Error("API não configurada (PRD_BFF_URL).");
  }
  const url = `${baseUrl}/cep/${digits}`;
  let res: Response;
  try {
    res = await fetch(url);
  } catch (e) {
    const msg =
      e instanceof TypeError && e.message?.includes("fetch")
        ? "Não foi possível conectar. Verifique sua conexão ou a URL do servidor (PRD_BFF_URL)."
        : "Não foi possível buscar o CEP. Tente novamente.";
    throw new Error(msg);
  }
  let data: CepResponse | CepNotFoundResponse | { error?: string };
  try {
    data = (await res.json()) as CepResponse | CepNotFoundResponse | { error?: string };
  } catch {
    throw new Error("Resposta inválida do servidor. Tente novamente.");
  }
  if (!res.ok) {
    const msg =
      data && typeof (data as { error?: string }).error === "string"
        ? (data as { error: string }).error
        : "Não foi possível buscar o CEP. Tente novamente.";
    throw new Error(msg);
  }
  if ("erro" in data && data.erro === true) {
    throw new Error("CEP não encontrado.");
  }
  const cepData = data as CepResponse;
  return {
    street: cepData.logradouro ?? "",
    neighborhood: cepData.bairro ?? "",
    city: cepData.localidade ?? "",
    state: cepData.uf ?? "",
  };
}
